import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Modalidade {
  id: string;
  nome: string;
  descricao: string | null;
  ordem: number;
  ativo: boolean;
  criado_em: string;
}

interface Tipo {
  id: string;
  nome: string;
  descricao: string | null;
  ordem: number;
  ativo: boolean;
  criado_em: string;
}

type Secao = 'modalidades' | 'tipos';

export default function DadosMestreTab() {
  const [secao, setSecao] = useState<Secao>('modalidades');
  const [modalidades, setModalidades] = useState<Modalidade[]>([]);
  const [tipos, setTipos] = useState<Tipo[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Modal states
  const [editModalidade, setEditModalidade] = useState<Modalidade | null>(null);
  const [showModalidadeAdd, setShowModalidadeAdd] = useState(false);
  const [editTipo, setEditTipo] = useState<Tipo | null>(null);
  const [showTipoAdd, setShowTipoAdd] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: modData, error: modErr }, { data: tipData, error: tipErr }] = await Promise.all([
      supabase.from('formacao_modalidades').select('*').order('ordem'),
      supabase.from('formacao_tipos').select('*').order('ordem'),
    ]);
    if (modErr) setError('Erro modalidades: ' + modErr.message);
    if (tipErr) setError('Erro tipos: ' + tipErr.message);
    setModalidades(modData ?? []);
    setTipos(tipData ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Modalidades ----
  const handleSaveModalidade = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editModalidade) return;
    setError('');
    setMsg('');

    const formData = new FormData(e.currentTarget);
    const data = {
      nome: String(formData.get('nome') ?? '').trim(),
      descricao: String(formData.get('descricao') ?? '').trim() || null,
      ordem: parseInt(String(formData.get('ordem') ?? '0'), 10) || 0,
    };

    if (!data.nome) {
      setError('Nome é obrigatório.');
      return;
    }

    const { error: err } = editModalidade.id
      ? await supabase.from('formacao_modalidades').update(data).eq('id', editModalidade.id)
      : await supabase.from('formacao_modalidades').insert({ ...data, ativo: true });

    if (err) {
      setError('Erro ao guardar: ' + err.message);
    } else {
      setMsg(editModalidade.id ? 'Modalidade atualizada!' : 'Modalidade criada!');
      setEditModalidade(null);
      setShowModalidadeAdd(false);
      await fetchData();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleToggleModalidade = async (m: Modalidade) => {
    setError('');
    const { error: err } = await supabase.from('formacao_modalidades').update({ ativo: !m.ativo }).eq('id', m.id);
    if (err) setError('Erro: ' + err.message);
    else { setMsg(`Modalidade "${m.nome}" ${!m.ativo ? 'ativada' : 'desativada'}.`); await fetchData(); setTimeout(() => setMsg(''), 4000); }
  };

  const handleDeleteModalidade = async (m: Modalidade) => {
    if (!confirm(`Eliminar modalidade "${m.nome}"?`)) return;
    setError('');
    const { error: err } = await supabase.from('formacao_modalidades').delete().eq('id', m.id);
    if (err) setError('Erro: ' + err.message);
    else { setMsg('Modalidade eliminada.'); await fetchData(); setTimeout(() => setMsg(''), 4000); }
  };

  // ---- Tipos ----
  const handleSaveTipo = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editTipo) return;
    setError('');
    setMsg('');

    const formData = new FormData(e.currentTarget);
    const data = {
      nome: String(formData.get('nome') ?? '').trim(),
      descricao: String(formData.get('descricao') ?? '').trim() || null,
      ordem: parseInt(String(formData.get('ordem') ?? '0'), 10) || 0,
    };

    if (!data.nome) {
      setError('Nome é obrigatório.');
      return;
    }

    const { error: err } = editTipo.id
      ? await supabase.from('formacao_tipos').update(data).eq('id', editTipo.id)
      : await supabase.from('formacao_tipos').insert({ ...data, ativo: true });

    if (err) {
      setError('Erro ao guardar: ' + err.message);
    } else {
      setMsg(editTipo.id ? 'Tipo atualizado!' : 'Tipo criado!');
      setEditTipo(null);
      setShowTipoAdd(false);
      await fetchData();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleToggleTipo = async (t: Tipo) => {
    setError('');
    const { error: err } = await supabase.from('formacao_tipos').update({ ativo: !t.ativo }).eq('id', t.id);
    if (err) setError('Erro: ' + err.message);
    else { setMsg(`Tipo "${t.nome}" ${!t.ativo ? 'ativado' : 'desativado'}.`); await fetchData(); setTimeout(() => setMsg(''), 4000); }
  };

  const handleDeleteTipo = async (t: Tipo) => {
    if (!confirm(`Eliminar tipo "${t.nome}"?`)) return;
    setError('');
    const { error: err } = await supabase.from('formacao_tipos').delete().eq('id', t.id);
    if (err) setError('Erro: ' + err.message);
    else { setMsg('Tipo eliminado.'); await fetchData(); setTimeout(() => setMsg(''), 4000); }
  };

  const renderLista = <T extends { id: string; nome: string; descricao: string | null; ordem: number; ativo: boolean }>(
    items: T[],
    onEdit: (item: T) => void,
    onToggle: (item: T) => void,
    onDelete: (item: T) => void,
    icon: string
  ) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-14">
          <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center gap-2 py-14">
          <div className="w-10 h-10 flex items-center justify-center bg-zinc-100 rounded-full">
            <i className={`${icon} text-zinc-400 text-lg`} />
          </div>
          <p className="text-[13px] text-zinc-400">Nenhum item configurado</p>
        </div>
      );
    }
    return (
      <div className="divide-y divide-zinc-50">
        {items.map((item) => (
          <div key={item.id} className="px-6 py-4 hover:bg-zinc-50/60 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${item.ativo ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                  <i className={`${item.ativo ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-indeterminate-circle-line text-zinc-400'} text-lg`} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[13px] font-semibold text-zinc-800">{item.nome}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${item.ativo ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-zinc-100 text-zinc-400 border border-zinc-200'}`}>
                      {item.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  {item.descricao && <p className="text-[11px] text-zinc-400 mt-0.5">{item.descricao}</p>}
                  <p className="text-[11px] text-zinc-400 mt-1">Ordem: {item.ordem}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => onToggle(item)} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer" title={item.ativo ? 'Desativar' : 'Ativar'}>
                  <i className={`${item.ativo ? 'ri-pause-line' : 'ri-play-line'} text-sm`} />
                </button>
                <button onClick={() => onEdit(item)} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer" title="Editar">
                  <i className="ri-edit-line text-sm" />
                </button>
                <button onClick={() => onDelete(item)} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer" title="Eliminar">
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-900 rounded-xl flex-shrink-0">
              <i className="ri-database-2-line text-white text-lg" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-zinc-800">Dados Mestre da Formação</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Gerir modalidades, tipos e outras categorias base do módulo de formação</p>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className="flex items-center gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-emerald-500 text-sm" /></div>
          <p className="text-[13px] text-emerald-700">{msg}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-red-500 text-sm" /></div>
          <p className="text-[13px] text-red-700">{error}</p>
        </div>
      )}

      {/* Tabs internas */}
      <div className="flex gap-1 bg-white border border-zinc-200 p-1 rounded-xl w-fit">
        <button
          onClick={() => setSecao('modalidades')}
          className={`flex items-center gap-2 px-4 py-2 text-[13px] font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${secao === 'modalidades' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50'}`}
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-presentation-line text-sm" /></div>
          Modalidades ({modalidades.length})
        </button>
        <button
          onClick={() => setSecao('tipos')}
          className={`flex items-center gap-2 px-4 py-2 text-[13px] font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${secao === 'tipos' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50'}`}
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line text-sm" /></div>
          Tipos ({tipos.length})
        </button>
      </div>

      {/* Secao Modalidades */}
      {secao === 'modalidades' && (
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                <i className="ri-presentation-line text-zinc-600 text-sm" />
              </div>
              <div>
                <h3 className="text-[14px] font-semibold text-zinc-800">Modalidades de Formação</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">Como a formação é ministrada (presencial, online, etc.)</p>
              </div>
            </div>
            <button
              onClick={() => { setEditModalidade({ id: '', nome: '', descricao: '', ordem: 0, ativo: true, criado_em: '' }); setShowModalidadeAdd(true); }}
              className="flex items-center gap-2 px-3 py-1.5 bg-zinc-900 text-white text-[12px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line text-xs" /></div>
              Nova
            </button>
          </div>
          {renderLista(modalidades, (m) => { setEditModalidade(m); setShowModalidadeAdd(true); }, handleToggleModalidade, handleDeleteModalidade, 'ri-presentation-line')}
        </div>
      )}

      {/* Secao Tipos */}
      {secao === 'tipos' && (
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                <i className="ri-stack-line text-zinc-600 text-sm" />
              </div>
              <div>
                <h3 className="text-[14px] font-semibold text-zinc-800">Tipos de Formação</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">Categorização da formação (interna, externa, certificação, etc.)</p>
              </div>
            </div>
            <button
              onClick={() => { setEditTipo({ id: '', nome: '', descricao: '', ordem: 0, ativo: true, criado_em: '' }); setShowTipoAdd(true); }}
              className="flex items-center gap-2 px-3 py-1.5 bg-zinc-900 text-white text-[12px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line text-xs" /></div>
              Novo
            </button>
          </div>
          {renderLista(tipos, (t) => { setEditTipo(t); setShowTipoAdd(true); }, handleToggleTipo, handleDeleteTipo, 'ri-stack-line')}
        </div>
      )}

      {/* Modal Modalidade */}
      {showModalidadeAdd && editModalidade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h3 className="text-[15px] font-semibold text-zinc-800">{editModalidade.id ? 'Editar Modalidade' : 'Nova Modalidade'}</h3>
              <button onClick={() => { setShowModalidadeAdd(false); setEditModalidade(null); }} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 rounded-lg transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSaveModalidade} className="p-6 space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Nome</label>
                <input name="nome" defaultValue={editModalidade.nome} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Ex: Presencial" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Descrição</label>
                <input name="descricao" defaultValue={editModalidade.descricao ?? ''} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Descrição opcional" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Ordem</label>
                <input name="ordem" type="number" defaultValue={editModalidade.ordem} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="0" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowModalidadeAdd(false); setEditModalidade(null); }} className="px-4 py-2 text-[13px] font-medium text-zinc-600 hover:text-zinc-800 transition-colors cursor-pointer">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer">{editModalidade.id ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Tipo */}
      {showTipoAdd && editTipo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h3 className="text-[15px] font-semibold text-zinc-800">{editTipo.id ? 'Editar Tipo' : 'Novo Tipo'}</h3>
              <button onClick={() => { setShowTipoAdd(false); setEditTipo(null); }} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 rounded-lg transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSaveTipo} className="p-6 space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Nome</label>
                <input name="nome" defaultValue={editTipo.nome} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Ex: Interna" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Descrição</label>
                <input name="descricao" defaultValue={editTipo.descricao ?? ''} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Descrição opcional" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Ordem</label>
                <input name="ordem" type="number" defaultValue={editTipo.ordem} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="0" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowTipoAdd(false); setEditTipo(null); }} className="px-4 py-2 text-[13px] font-medium text-zinc-600 hover:text-zinc-800 transition-colors cursor-pointer">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer">{editTipo.id ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}